import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * In-card quick add.
 *
 * The plus button sits over the bottom of the card gallery. On desktop it slides
 * up on hover (CSS only); on mobile it is always visible. Pressing it opens a
 * panel over the lower third of the card containing the size run. Picking a size
 * writes that variant id into the product form's hidden input and arms the
 * price bar, which is the form's submit button.
 *
 * Single-variant products render no panel at all — the plus is the submit button.
 */
class StlnQuickAddComponent extends Component {
  /** @type {((event: Event) => void) | null} */
  #outsidePointerHandler = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    this.#teardownOutsideHandler();
    this.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    super.disconnectedCallback();
  }

  get panel() {
    return this.querySelector('[data-stln-panel]');
  }

  get trigger() {
    return this.querySelector('[data-stln-trigger]');
  }

  get bar() {
    return this.querySelector('[data-stln-bar]');
  }

  get barLabel() {
    return this.querySelector('[data-stln-bar-label]');
  }

  get variantInput() {
    return this.querySelector('input[name="id"]');
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Opens the size panel. Bound via on:click on the plus button.
   * @param {Event} [event]
   */
  open(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const { panel } = this;
    if (!panel || this.isOpen) return;

    for (const other of document.querySelectorAll('stln-quick-add-component[open]')) {
      if (other !== this && typeof other.close === 'function') other.close();
    }

    panel.hidden = false;
    this.setAttribute('open', '');
    this.trigger?.setAttribute('aria-expanded', 'true');

    this.#outsidePointerHandler = (pointerEvent) => {
      const target = pointerEvent.target;
      if (target instanceof Node && this.contains(target)) return;
      this.close();
    };

    // Deferred so the click that opened the panel does not immediately close it.
    requestAnimationFrame(() => {
      if (this.#outsidePointerHandler) {
        document.addEventListener('pointerdown', this.#outsidePointerHandler);
      }
    });

    panel.querySelector('[data-stln-size]:not([disabled])')?.focus();
  }

  /**
   * Closes the size panel. Bound via on:click on the close button.
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const { panel } = this;
    if (!panel) return;

    panel.hidden = true;
    this.removeAttribute('open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.#teardownOutsideHandler();
  }

  /**
   * Selects a size. Arms the price bar rather than adding straight to cart —
   * the bar is the second tap.
   * @param {Event} event
   */
  selectVariant(event) {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-stln-size]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    for (const size of this.querySelectorAll('[data-stln-size]')) {
      const selected = size === button;
      size.setAttribute('aria-pressed', String(selected));
      size.classList.toggle('stln-quick-add__size--selected', selected);
    }

    const input = this.variantInput;
    if (input instanceof HTMLInputElement && button.dataset.variantId) {
      input.value = button.dataset.variantId;
      input.disabled = false;
    }

    const { barLabel, bar } = this;
    if (barLabel?.dataset.addLabel) barLabel.textContent = barLabel.dataset.addLabel;
    if (bar instanceof HTMLButtonElement) bar.disabled = false;

    this.setAttribute('has-selection', '');
  }

  #teardownOutsideHandler() {
    if (!this.#outsidePointerHandler) return;
    document.removeEventListener('pointerdown', this.#outsidePointerHandler);
    this.#outsidePointerHandler = null;
  }

  #handleCartUpdate = () => {
    if (this.isOpen) this.close();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    this.close();
    if (this.trigger instanceof HTMLElement) this.trigger.focus();
  };
}

if (!customElements.get('stln-quick-add-component')) {
  customElements.define('stln-quick-add-component', StlnQuickAddComponent);
}
